(() => {
  // src/rates.js
  var EU = "AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE".split(" ");
  var COUNTRIES = ["AU", "US", "CA", "GB", "NZ", ...EU];
  var marketFor = (country) => EU.includes(country) ? "EU" : country;
  var cleanPostal = (value) => String(value ?? "").trim().toUpperCase().replace(/[\s-]/g, "");
  var postalLabel = (country) => country === "US" ? "ZIP code" : ["AU", "GB", "NZ"].includes(country) ? "Postcode" : "Postal code";
  function validPostal(country, value) {
    const code = cleanPostal(value);
    if (country === "US") return /^\d{5}(\d{4})?$/.test(code);
    if (country === "AU" || country === "NZ") return /^\d{4}$/.test(code);
    if (country === "CA") return /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTVWXYZ]\d[ABCEGHJ-NPRSTVWXYZ]\d$/.test(code);
    if (country === "GB") return /^(GIR0AA|[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2})$/.test(code);
    return EU.includes(country) && /^[A-Z\d]{3,10}$/.test(code);
  }
  function defaultConfig() {
    return { schemaVersion: 1, revision: "unconfigured", enabled: true, products: [], rates: [], markets: Object.fromEntries(["AU", "US", "CA", "GB", "EU", "NZ"].map((code) => [code, { enabled: true, currency: { AU: "AUD", US: "USD", CA: "CAD", GB: "GBP", EU: "EUR", NZ: "NZD" }[code], window: "", assemblyEnabled: ["AU", "US"].includes(code) }])) };
  }
  var productId = (value) => String(value || "").split("/").pop();
  var current = (row, date) => (!row.effective_from || row.effective_from <= date) && (!row.effective_to || row.effective_to >= date);
  function matches(row, postal) {
    if (row.match_type === "all") return true;
    if (row.match_type === "exact") return cleanPostal(row.postal_from) === postal;
    if (row.match_type === "prefix") return postal.startsWith(cleanPostal(row.postal_prefix));
    return row.match_type === "range" && /^\d+$/.test(postal) && postal.length === row.postal_from.length && Number(postal) >= Number(row.postal_from) && Number(postal) <= Number(row.postal_to);
  }
  function priority(row) {
    return (row.product_id ? 1e6 : 0) + (row.match_type === "exact" ? 1e5 : row.match_type === "prefix" ? 1e4 + cleanPostal(row.postal_prefix).length : row.match_type === "range" ? 1e3 + 1 / (1 + Number(row.postal_to) - Number(row.postal_from)) : 0);
  }
  function lookup(config, { country, postal, product, currency, date = (/* @__PURE__ */ new Date()).toISOString().slice(0, 10) }) {
    country = String(country).toUpperCase();
    postal = cleanPostal(postal);
    if (country === "US" && postal.length === 9) postal = postal.slice(0, 5);
    const market = config.markets?.[marketFor(country)];
    const empty = (status) => ({ status, amount: null, currency });
    const result = { country, postal, revision: config.revision, window: market?.window || "", delivery: empty("quote_required"), assembly: empty("not_offered") };
    if (!config.enabled || !market?.enabled || config.products?.length && !config.products.some((id) => productId(id) === productId(product))) return { ...result, status: "disabled" };
    if (!validPostal(country, postal)) return { ...result, status: "invalid_postal" };
    if (currency !== market.currency) return { ...result, status: "currency_mismatch" };
    for (const service of ["delivery", "assembly"]) {
      if (service === "assembly" && !market.assemblyEnabled) continue;
      const candidates = (config.rates || []).filter((row) => row.service === service && row.country_code === country && (!row.product_id || productId(row.product_id) === productId(product)) && row.currency === currency && current(row, date) && matches(row, postal));
      const excluded = candidates.some((row) => row.status === "not_offered");
      candidates.sort((a, b) => priority(b) - priority(a));
      const selected = candidates[0];
      if (excluded) result[service] = empty("not_offered");
      else if (!selected) result[service] = empty("quote_required");
      else if (candidates[1] && priority(selected) === priority(candidates[1])) result[service] = empty("conflict");
      else result[service] = { status: selected.status, amount: selected.status === "offered" ? Math.round(Number(selected.amount) * 100) : null, currency, note: selected.note || "" };
    }
    return { ...result, status: "ready" };
  }
  function minimumAssembly(config, { country, product, currency, date = (/* @__PURE__ */ new Date()).toISOString().slice(0, 10) }) {
    if (!config.enabled || !config.markets?.[marketFor(country)]?.assemblyEnabled) return null;
    const amounts = (config.rates || []).filter((r) => r.service === "assembly" && r.country_code === country && r.currency === currency && r.status === "offered" && (!r.product_id || productId(r.product_id) === productId(product)) && current(r, date)).map((r) => Math.round(Number(r.amount) * 100)).filter(Number.isSafeInteger);
    return amounts.length ? Math.min(...amounts) : null;
  }

  // src/storefront.js
  var KindraDeliveryAssembly = class extends HTMLElement {
    attach(parent) {
      if (this.parentController) return;
      this.config = defaultConfig();
      try {
        const data = JSON.parse(this.querySelector("[data-kda-config]").textContent);
        if (data?.schemaVersion === 1) this.config = data;
      } catch {
      }
      this.country = this.dataset.country;
      this.currency = parent.data.currency;
      this.market = this.config.markets?.[marketFor(this.country)];
      if (!this.config.enabled || !this.market?.enabled) return;
      if (this.config.products?.length && !this.config.products.some((id) => String(id).split("/").pop() === String(parent.data.id))) return;
      this.parentController = parent;
      this.abort = new AbortController();
      this.stateMode = this.country === "AU";
      parent.data.quoteAssembly = !this.stateMode;
      if (this.stateMode) {
        try {
          this.stateRates = JSON.parse(this.querySelector("[data-kda-state-rates]").textContent);
        } catch {
          this.stateRates = {};
        }
      }
      this.querySelector("[data-kda-postal-label]").textContent = postalLabel(this.country);
      this.input = this.querySelector("[data-kda-postal]");
      this.input.inputMode = ["AU", "NZ", "US"].includes(this.country) ? "numeric" : "text";
      if (this.stateMode) {
        const select = document.createElement("select");
        select.id = this.input.id;
        select.setAttribute("data-kda-postal", "");
        select.setAttribute("autocomplete", "address-level1");
        select.setAttribute("aria-label", "State / territory");
        for (const [value, label] of [["", "Select your state"], ["ACT", "Australian Capital Territory"], ["NSW", "New South Wales"], ["NT", "Northern Territory"], ["QLD", "Queensland"], ["SA", "South Australia"], ["TAS", "Tasmania"], ["VIC", "Victoria"], ["WA", "Western Australia"]]) {
          const option = document.createElement("option");
          option.value = value;
          option.textContent = label;
          select.append(option);
        }
        const returned = parent.data.purchaseEnquiry ? new URL(window.location.href).searchParams : null;
        const requestedState = returned?.has('contact_posted') && returned.has('ke_state') ? returned.get('ke_state') : null;
        select.value = requestedState !== null && ['', 'ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'].includes(requestedState) ? requestedState : this.dataset.defaultState || "";
        this.input.replaceWith(select);
        this.input = select;
        this.querySelector("[data-kda-postal-label]").hidden = true;
        this.querySelector("[data-kda-check]").hidden = true;
        select.addEventListener("change", () => this.check(), { signal: this.abort.signal });
      }
      parent.querySelectorAll("kindra-delivery-windows").forEach((widget) => {
        const wrapper = widget.closest('[data-ke-block-type="@app"]') || widget;
        wrapper.hidden = true;
      });
      this.querySelector("[data-kda-check]").addEventListener("click", () => this.check(), { signal: this.abort.signal });
      this.input.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          this.check();
        }
      }, { signal: this.abort.signal });
      this.input.addEventListener("input", () => {
        if (this.stateMode) return;
        this.result = null;
        this.input.removeAttribute("aria-invalid");
        if (parent.state.assembly) parent.select({ assembly: false });
        this.refresh();
        parent.updateConfigurationPrice();
      }, { signal: this.abort.signal });
      parent.quoteDialog?.querySelector('[name="contact[Postal code]"]')?.addEventListener("input", (e) => {
        if (!this.stateMode && cleanPostal(e.target.value) !== this.result?.postal) {
          this.input.value = e.target.value;
          this.result = null;
          if (parent.state.assembly) parent.select({ assembly: false });
          this.refresh();
          parent.updateConfigurationPrice();
        }
      }, { signal: this.abort.signal });
      this.hidden = false;
      if (this.stateMode && this.input.value) {
        this.check();
        return;
      }
      this.refresh();
      parent.renderAssemblyChoices();
    }
    async check() {
      const parent = this.parentController;
      const input = this.input.value, requestId = this.requestId = (this.requestId || 0) + 1;
      if (this.stateMode) {
        const amount = this.stateRates?.rates?.[input];
        this.result = input ? { status: this.currency === this.stateRates.currency ? "ready" : "currency_mismatch", delivery: { status: Number.isSafeInteger(amount) && this.currency === this.stateRates.currency ? "offered" : "quote_required", amount, currency: this.currency }, assembly: this.backendAssembly(), window: this.stateWindow() } : null;
        if (!this.assemblyAllowed() && parent.state.assembly) parent.select({ assembly: false });
        this.refresh();
        parent.renderAssemblyChoices();
        parent.updateConfigurationPrice();
        return;
      }
      if (this.config.serviceUrl) {
        const button = this.querySelector("[data-kda-check]");
        button.disabled = true;
        this.result = null;
        this.refresh();
        parent.updateConfigurationPrice();
        try {
          const endpoint = new URL(this.config.serviceUrl);
          if (endpoint.protocol !== "https:") throw Error("HTTPS required");
          const response = await fetch(endpoint, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ country: this.country, postal: input, currency: this.currency, items: [{ product: parent.data.id, quantity: 1, assembly: false }] }), signal: AbortSignal.timeout(5e3) });
          if (!response.ok) throw Error("Unavailable");
          const data = await response.json();
          if (requestId !== this.requestId || this.input.value !== input) return;
          if (!data.detail || data.detail.country !== this.country || data.detail.delivery?.currency !== this.currency) throw Error("Invalid response");
          this.result = data.detail;
        } catch {
          if (requestId === this.requestId && this.input.value === input) {
            this.result = null;
            this.refresh();
            this.querySelector("[data-kda-status]").textContent = "Delivery pricing is temporarily unavailable. Please request a quote.";
          }
          return;
        } finally {
          if (requestId === this.requestId) button.disabled = false;
        }
      } else {
        this.result = lookup(this.config, { country: this.country, postal: input, product: parent.data.id, currency: this.currency });
      }
      if (this.result.status === "invalid_postal") {
        this.refresh();
        parent.updateConfigurationPrice();
        this.input.setAttribute("aria-invalid", "true");
        this.querySelector("[data-kda-status]").textContent = `Enter a valid ${postalLabel(this.country).toLowerCase()}.`;
        this.input.focus();
        return;
      }
      if (parent.state.assembly && this.result.assembly.status === "not_offered") parent.select({ assembly: false });
      const quotePostal = parent.quoteDialog?.querySelector('[name="contact[Postal code]"]');
      if (quotePostal) quotePostal.value = this.input.value.trim().toUpperCase();
      this.input.removeAttribute("aria-invalid");
      this.refresh();
      parent.renderAssemblyChoices();
      parent.updateConfigurationPrice();
    }
    stateWindow(includePrefix = true) {
      if (!this.stateMode || !this.input?.value) return "";
      try {
        const node = this.parentController.querySelector("kindra-delivery-windows [data-kdw-config]");
        const config = JSON.parse(node?.textContent || "null");
        if (!config?.enabled || !config.assignedProductLegacyIds?.some((id) => productId(id) === productId(this.parentController.data.id))) return "";
        const cluster = config.clusters?.find((item) => item.enabled !== false && item.states?.includes(this.input.value));
        if (!cluster) return "";
        const schedule = config.productSchedules?.[productId(this.parentController.data.id)];
        const deliveryWindow = [schedule?.states?.[this.input.value], schedule?.window, cluster.window].find((value) => typeof value === "string" && value.trim())?.trim() || "";
        if (!deliveryWindow) return "";
        return includePrefix ? `${config.copy?.windowPrefix?.trim() || "Delivers"} ${deliveryWindow}` : deliveryWindow;
      } catch {
        return "";
      }
    }
    backendAssembly() {
      const parent = this.parentController, assembled = parent.find({ assembly: true }), self = parent.find({ assembly: false });
      const amount = assembled && self ? assembled.price - self.price : null;
      return { status: this.assemblyAllowed() && Number.isSafeInteger(amount) && amount >= 0 ? "offered" : "not_offered", amount, currency: this.currency };
    }
    deliveryLabel(item) {
      return item?.status === "offered" && item.amount === 0 ? "Complimentary" : this.amountLabel(item);
    }
    amountLabel(item) {
      if (item?.status === "offered") return this.parentController.money(item.amount) + " " + this.currency;
      if (item?.status === "not_offered") return "Not offered at this location";
      if (item?.status === "conflict") return "Quote required";
      return "Quote required";
    }
    assemblyAllowed() {
      if (this.stateMode) {
        if (this.input?.value && !Number.isSafeInteger(this.stateRates?.rates?.[this.input.value])) return false;
        const variant = this.parentController.find({ assembly: true });
        return this.parentController.data.expertAssemblyAvailable !== false && !!variant && !!(variant.available || this.parentController.data.quoteMode);
      }
      return !!this.market?.assemblyEnabled && this.result?.assembly?.status !== "not_offered";
    }
    refreshBatchSchedule(deliveryWindow, visible) {
      const schedule = this.querySelector('[data-kda-schedule]');
      if (!schedule) return;
      const rows = Array.from(schedule.querySelectorAll('[data-kda-batch-date]'));
      const status = this.querySelector('[data-kda-order-status]');
      const previous = this.querySelector('[data-kda-previous-batches]');
      const dates = this.querySelector('[data-kda-previous-dates]');
      const normalise = value => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
      const available = rows.filter(row => row.dataset.kdaBatchStatus === 'taking_orders');
      const combined = !!(status && visible && available.length === 1 && normalise(deliveryWindow) &&
        normalise(available[0].dataset.kdaBatchDate) === normalise(deliveryWindow));
      const soldOut = rows.filter(row => row.dataset.kdaBatchStatus === 'sold_out');
      const compactHistory = !!(combined && previous && dates && soldOut.length);
      if (status) status.hidden = !combined;
      if (previous) previous.hidden = !compactHistory;
      if (dates) dates.textContent = compactHistory ? soldOut.map(row => row.dataset.kdaBatchDate.trim()).join(' · ') : '';
      rows.forEach(row => {
        row.hidden = (combined && row === available[0]) || (compactHistory && row.dataset.kdaBatchStatus === 'sold_out');
      });
      schedule.hidden = rows.every(row => row.hidden);
    }
    refresh() {
      if (!this.parentController) return;
      const parent = this.parentController;
      const tile = this.querySelector(".kda__tile");
      const previousHeight = tile?.getBoundingClientRect().height;
      const delivery = this.querySelector(".kda__delivery");
      const selectedState = this.stateMode ? this.input.value : "";
      const stateChanged = this.displayedState !== selectedState;
      let outgoing;
      if (stateChanged) {
        this.deliveryFade?.cancel();
        this.deliverySnapshot?.remove();
        if (this.expanded && selectedState && delivery?.cloneNode &&
            !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
          outgoing = delivery.cloneNode(true);
          outgoing.classList.add("kda__delivery-snapshot");
          outgoing.setAttribute("aria-hidden", "true");
          outgoing.removeAttribute("aria-live");
          outgoing.inert = true;
          outgoing.querySelectorAll("[data-kda-window], [data-kda-delivery], [data-kda-order-status]").forEach((node) => {
            if (node.hasAttribute("data-kda-window")) node.classList.add("kda__window-snapshot");
            if (node.hasAttribute("data-kda-delivery")) node.classList.add("kda__cost-snapshot");
            node.removeAttribute("data-kda-window");
            node.removeAttribute("data-kda-delivery");
            node.removeAttribute("data-kda-order-status");
          });
        }
      }
      this.displayedState = selectedState;
      if (this.stateMode && this.result) this.result.assembly = this.backendAssembly();
      const expanded = !!this.result && ["ready", "currency_mismatch"].includes(this.result.status);
      const results = this.querySelector("[data-kda-results]");
      if (results) results.hidden = !expanded;
      this.querySelector("[data-kda-check]").setAttribute("aria-expanded", String(expanded));
      const min = minimumAssembly(this.config, { country: this.country, product: parent.data.id, currency: this.currency });
      this.querySelector("[data-kda-delivery]").textContent = this.result ? this.deliveryLabel(this.result.delivery) : this.stateMode ? "Select your state" : `Enter your ${postalLabel(this.country)}`;
      const timing = this.querySelector("[data-kda-window]");
      const deliveryWindow = this.stateMode ? this.stateWindow() : this.result?.window || this.market?.window || "";
      if (this.stateMode && this.result) this.result.window = deliveryWindow;
      timing.textContent = this.stateMode ? this.stateWindow(false) : deliveryWindow;
      timing.hidden = !timing.textContent;
      this.refreshBatchSchedule(timing.textContent, expanded);
      const timingDetail = parent.deliveryDialog?.querySelector("[data-kda-window-detail]");
      if (timingDetail) {
        timingDetail.textContent = deliveryWindow;
        timingDetail.hidden = !timingDetail.textContent;
      }
      this.querySelector("[data-kda-status]").textContent = this.result?.status === "currency_mismatch" ? "Pricing needs confirmation for the selected currency." : "";
      const costDetail = parent.deliveryDialog?.querySelector("[data-kda-cost-detail]");
      if (costDetail) costDetail.textContent = this.result ? this.deliveryLabel(this.result.delivery) : "";
      const deliverySummary = parent.deliveryDialog?.querySelector("[data-kda-delivery-summary]");
      if (deliverySummary) deliverySummary.hidden = !expanded;
      const expert = parent.querySelector('[data-ke-assembly="true"]');
      if (expert) {
        expert.disabled = !this.assemblyAllowed();
        expert.querySelector("[data-ke-assembly-cost]").textContent = this.stateMode ? this.backendAssembly().status === "offered" ? "+ " + parent.money(this.backendAssembly().amount) : "Unavailable" : !this.market?.assemblyEnabled ? "Unavailable" : this.result ? this.amountLabel(this.result.assembly) : min !== null ? "From + " + parent.money(min) : "Price by quote";
      }
      if (tile && previousHeight && this.expanded !== undefined && (this.expanded !== expanded || stateChanged) &&
          !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        this.expansion?.cancel();
        this.expansion = tile.animate([
          { height: previousHeight + "px", overflow: "clip" },
          { height: tile.getBoundingClientRect().height + "px", overflow: "clip" }
        ], { duration: 550, easing: "cubic-bezier(.4, 0, .2, 1)" });
      }
      if (outgoing && expanded) {
        results.append(outgoing);
        this.deliverySnapshot = outgoing;
        const options = { duration: 300, easing: "ease-in-out" };
        this.deliveryFade = delivery.animate([{ opacity: 0 }, { opacity: 1 }], options);
        const fadeOut = outgoing.animate([{ opacity: 1 }, { opacity: 0 }], options);
        fadeOut.finished.then(() => outgoing.remove(), () => outgoing.remove());
      }
      this.expanded = expanded;
    }
    estimates() {
      const parent = this.parentController, result = this.result;
      const delivery = result?.status === "ready" && result.delivery.status === "offered" ? result.delivery.amount : null;
      const assembly = parent.state.assembly ? result?.status === "ready" && result.assembly.status === "offered" ? result.assembly.amount : null : 0;
      if (this.stateMode) return { delivery, assembly: parent.state.assembly ? this.backendAssembly().amount : 0, subtotal: parent.variant?.price, total: (parent.variant?.price || 0) + (delivery ?? 0), due: parent.due || 0, saunaBalance: (parent.variant?.price || 0) - (parent.due || 0) };
      return { delivery, assembly, subtotal: parent.variant?.price, total: (parent.variant?.price || 0) + (delivery ?? 0) + (assembly ?? 0), due: 0, saunaBalance: 0 };
    }
    updateQuote() {
      const dialog = this.parentController?.quoteDialog;
      if (!dialog) return;
      const delivery = dialog.querySelector(".ke__quote-delivery");
      if (delivery) delivery.lastElementChild.textContent = this.result ? this.deliveryLabel(this.result.delivery) : "Quote required";
      let row = dialog.querySelector("[data-kda-quote-assembly]");
      if (!row && delivery) {
        row = document.createElement("div");
        row.className = "ke__quote-delivery";
        row.dataset.kdaQuoteAssembly = "";
        row.innerHTML = "<span>Assembly estimate</span><span></span>";
        delivery.after(row);
      }
      if (row) row.lastElementChild.textContent = this.parentController.state.assembly ? this.stateMode ? this.amountLabel(this.backendAssembly()) : this.result ? this.amountLabel(this.result.assembly) : "Quote required" : "Self Assembly";
    }
    quoteLines() {
      if (!this.parentController) return [];
      const result = this.result;
      return [`Delivery country: ${this.country}`, `${this.stateMode ? "State / territory" : postalLabel(this.country)}: ${this.input.value || "Not checked"}`, `Delivery estimate: ${result ? this.deliveryLabel(result.delivery) : "Quote required"}`, `Assembly estimate: ${this.parentController.state.assembly ? result ? this.amountLabel(result.assembly) : "Quote required" : "Self Assembly"}`, `Delivery window: ${(this.stateMode ? this.stateWindow() : result?.window || this.market?.window) || "To be confirmed"}`, `Rate revision: ${this.config.revision}`, "All service estimates require confirmation before payment."];
    }
    disconnectedCallback() {
      this.expansion?.cancel();
      this.abort?.abort();
    }
  };
  if (!customElements.get("kindra-delivery-assembly")) customElements.define("kindra-delivery-assembly", KindraDeliveryAssembly);
})();


